import { create, type StoreApi, type UseBoundStore } from 'zustand';

import { getQueryClient } from 'app/get-query-client';
import {
    deriveSharingLevel,
    getSharingLevelTargets,
    type DerivedSharingLevel,
    MANAGED_SHARING_KEYS,
    type SharingLevel,
} from 'constants/socialSharingLevels';
import { useAuthStore } from 'lib/stores/authStore';
import type { ServerError, SocialPreferencesVo } from 'models';
import {
    ownSocialProfileQueryOptions,
    updateGeneratedProfileImage,
    updatePendingProfileImage,
    updateProfileNickname,
    type GetSocialProfileMetadataResponse,
} from 'queries/socialProfile';
import {
    setSocialPreference,
    socialPreferencesKeys,
    socialPreferencesQueryOptions,
} from 'queries/socialProfilePreferences';
import { handleSnackbarError } from 'stores/zustand/snackbarStore';
import analyticsProvider from 'utils/analytics/analyticsProvider';
import { getErrorMessage } from 'utils/errors';
import { compressImage, getIdeasProfileImageUrl, hasRealProfileImage } from 'utils/images';
import { generateNicknameSuggestions } from 'utils/nicknameSuggestions';
import { getGeneratedProfileImageUrl, shuffleIndexFromProfileImagePath } from 'utils/pfpGenerator';

export type SocialOnboardingStep = 'intro' | 'nickname' | 'preferences' | 'avatar';

/** New users default to sharing the most ("Full") — they can dial it back here or later. */
const DEFAULT_SHARING_LEVEL: SharingLevel = 'full';

interface SocialOnboardingOpenOptions {
    onComplete?: () => void;
}

interface SocialOnboardingState {
    isOpen: boolean;
    step: SocialOnboardingStep;
    nicknameInput: string;
    nicknameError: string;
    nicknameSuggestions: string[];
    isNicknameSubmitting: boolean;
    selectedLevel: SharingLevel;
    /**
     * The stored level as it actually is, for change detection only -- `custom` when the stored
     * map matches none of the three. `selectedLevel` stays a writable `SharingLevel`.
     */
    initialLevel: DerivedSharingLevel;
    metricsError: string;
    isSubmittingPreferences: boolean;
    shouldForcePreferencesSubmission: boolean;
    generatedAvatarUrl: string;
    shuffleIndex: number;
    pendingAvatarDataUrl: string | null;
    avatarError: string;
    isSubmittingAvatar: boolean;
    actions: {
        open: (options?: SocialOnboardingOpenOptions) => void;
        close: (executePendingAction?: boolean) => void;
        cancel: () => void;
        dispose: () => void;
        startNicknameStep: () => void;
        setNickname: (value: string) => void;
        selectSuggestion: (value: string) => void;
        refreshSuggestions: () => void;
        submitNickname: () => Promise<void>;
        setLevel: (level: SharingLevel) => void;
        submitPreferences: () => Promise<void>;
        shuffleAvatar: () => void;
        selectAvatarFromFile: (file: File) => Promise<void>;
        submitAvatar: () => Promise<void>;
    };
}

// Internal-only, never read by any consumer — kept out of state so setting them doesn't trigger re-renders.
let avatarChanged = false;
// Invalidated by dispose() on session teardown, so async work started by one user
// cannot land in the next user's onboarding flow.
let flowGeneration = 0;
let pendingAction: (() => void) | null = null;

const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        // reader.error is DOMException | null — a null rejection reason would surface
        // in Datadog as an unattributable `Uncaught "Empty reason"` event.
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read avatar file'));
        reader.readAsDataURL(file);
    });

/**
 * The user's true current level (server-derived), used for change detection.
 *
 * `DerivedSharingLevel`, so a stored map matching no level reports `custom` rather than being
 * flattened into a real level -- this value is only ever compared against the selection to decide
 * whether the user changed anything, never written.
 */
const resolveInitialLevel = (preferences: SocialPreferencesVo | undefined): DerivedSharingLevel =>
    preferences ? deriveSharingLevel(preferences) : DEFAULT_SHARING_LEVEL;

/**
 * The level to pre-select. Returning users with prefs already set see their
 * derived level; brand-new profiles (no managed key on yet) default to Full.
 *
 * A stored map that matches no level cannot be pre-selected -- this value *is* what Continue
 * writes. It falls back to `minimum`, NOT `DEFAULT_SHARING_LEVEL`: the default exists for a blank
 * profile that has chosen nothing yet, whereas a `custom` map belongs to someone who has already
 * configured their visibility elsewhere, and pre-selecting Full for them would publish activity,
 * portfolio and every leaderboard metric on a blind Continue. `minimum` is the only pre-selection
 * that cannot escalate what they are already sharing.
 */
const resolveSelectedLevel = (preferences: SocialPreferencesVo | undefined): SharingLevel => {
    const hasStoredPrefs = preferences ? MANAGED_SHARING_KEYS.some((key) => preferences[key]) : false;
    if (!hasStoredPrefs || !preferences) return DEFAULT_SHARING_LEVEL;
    const derived = deriveSharingLevel(preferences);
    return derived === 'custom' ? 'minimum' : derived;
};

/**
 * Emit the chosen privacy level when the user advances past this step (to
 * GTM + Amplitude). `changed` distinguishes an explicit pick from continuing
 * on the pre-selected/derived level, so we can measure the distribution
 * across Full / Limited / Minimum and the opt-in/opt-out rate.
 */
const trackPrivacyLevel = (level: DerivedSharingLevel, changed: boolean): void => {
    analyticsProvider.sendEvent('SocialOnboardingPrivacyLevelSet', { level, changed });
};

/**
 * Read the logged-in user's prefs from the React Query cache (fetching if
 * absent). Returns undefined when logged out.
 */
const fetchPreferences = async (): Promise<SocialPreferencesVo | undefined> => {
    const userId = useAuthStore.getState().userId;
    if (!userId) return undefined;
    return getQueryClient().ensureQueryData(socialPreferencesQueryOptions(userId));
};

const updatePreference = async (option: keyof SocialPreferencesVo, enabled: boolean): Promise<void> => {
    const userId = useAuthStore.getState().userId;
    if (!userId) {
        throw new Error('Cannot update social preference when logged out');
    }
    await setSocialPreference(userId, option, enabled);
};

/**
 * Invalidate AND force-refetch the own-profile query. `invalidateQueries`
 * alone only marks an inactive query (no mounted observer — e.g. after the
 * signup survey navigates away) stale without refetching it, so a later
 * `getQueryData` read (from `requireSocialProfile`, `resolveInitialAvatar`,
 * etc.) can still see the pre-mutation cached value. The explicit
 * `fetchQuery` guarantees the cache holds the server's current response.
 *
 * Best-effort: called after the nickname/avatar mutation has already
 * succeeded, so a transient failure here (unlike `invalidateQueries`,
 * `fetchQuery` rejects on error) must not surface as a save error and
 * strand the user on the onboarding step.
 */
const refreshOwnSocialProfile = async (userId: string | null): Promise<void> => {
    const options = ownSocialProfileQueryOptions(userId);
    await getQueryClient().invalidateQueries({ queryKey: options.queryKey });
    try {
        await getQueryClient().fetchQuery(options);
    } catch {
        // Invalidation above still leaves the query stale for the next observer to refetch.
    }
};

const resetState = (): Partial<SocialOnboardingState> => {
    avatarChanged = false;
    pendingAction = null;
    return {
        isOpen: false,
        step: 'intro',
        nicknameInput: '',
        nicknameError: '',
        nicknameSuggestions: generateNicknameSuggestions(),
        isNicknameSubmitting: false,
        selectedLevel: DEFAULT_SHARING_LEVEL,
        initialLevel: DEFAULT_SHARING_LEVEL,
        metricsError: '',
        isSubmittingPreferences: false,
        shouldForcePreferencesSubmission: false,
        generatedAvatarUrl: '',
        shuffleIndex: 0,
        pendingAvatarDataUrl: null,
        avatarError: '',
        isSubmittingAvatar: false,
    };
};

const resolveInitialAvatar = (): Partial<SocialOnboardingState> => {
    const userId = useAuthStore.getState().userId;
    const cachedSocialProfile = getQueryClient().getQueryData<GetSocialProfileMetadataResponse>(
        ownSocialProfileQueryOptions(userId).queryKey,
    );
    const profileImagePath = cachedSocialProfile?.social_profile?.profile_image_path ?? null;
    const nickname =
        cachedSocialProfile?.social_profile?.nickname ||
        useSocialOnboardingStore.getState().nicknameInput ||
        userId ||
        'kalshi';

    const shuffleIndex = shuffleIndexFromProfileImagePath(profileImagePath) ?? 0;

    if (hasRealProfileImage(profileImagePath)) {
        return { shuffleIndex, generatedAvatarUrl: getIdeasProfileImageUrl(profileImagePath) };
    }

    return { shuffleIndex, generatedAvatarUrl: getGeneratedProfileImageUrl(nickname, 0) };
};

const advanceToAvatarStep = (): void => {
    useSocialOnboardingStore.setState({ ...resolveInitialAvatar(), step: 'avatar' });
};

export const useSocialOnboardingStore: UseBoundStore<StoreApi<SocialOnboardingState>> = create<SocialOnboardingState>()(
    (set, get) => ({
        ...(resetState() as SocialOnboardingState),
        actions: {
            open: (options = {}) => {
                if (get().isOpen) {
                    pendingAction = options.onComplete ?? pendingAction;
                    return;
                }
                set(resetState());
                pendingAction = options.onComplete ?? null;
                set({ isOpen: true });
            },
            close: (executePendingAction = false) => {
                const callback = pendingAction;
                set(resetState());
                if (executePendingAction && callback) {
                    callback();
                }
            },
            cancel: () => {
                get().actions.close(false);
            },
            dispose: () => {
                // Invalidates async work already in flight (see flowGeneration) so it
                // cannot repopulate the state this reset clears.
                flowGeneration += 1;
                set(resetState());
            },
            startNicknameStep: () => {
                if (!get().isOpen) return;
                set({ step: 'nickname' });
            },
            setNickname: (value) => {
                const trimmedValue = value.trim();
                let nicknameError = '';
                if (trimmedValue.length > 0) {
                    if (trimmedValue.length < 3) {
                        nicknameError = 'Must be at least 3 characters';
                    } else if (trimmedValue.length > 32) {
                        nicknameError = 'Must be 32 characters or less';
                    } else if (!/^[a-zA-Z0-9.]+$/.test(trimmedValue)) {
                        nicknameError = 'Only letters, numbers, and periods allowed';
                    }
                }
                set({ nicknameInput: value, nicknameError });
            },
            selectSuggestion: (value) => {
                set({
                    nicknameInput: value,
                    nicknameError: '',
                    nicknameSuggestions: generateNicknameSuggestions({ exclude: value }),
                });
            },
            refreshSuggestions: () => {
                set({ nicknameSuggestions: generateNicknameSuggestions({ exclude: get().nicknameInput }) });
            },
            submitNickname: async () => {
                const nickname = get().nicknameInput.trim();

                if (nickname.length < 3) {
                    set({ nicknameError: 'Must be at least 3 characters' });
                    return;
                }
                if (nickname.length > 32) {
                    set({ nicknameError: 'Must be 32 characters or less' });
                    return;
                }
                if (!/^[a-zA-Z0-9.]+$/.test(nickname)) {
                    set({ nicknameError: 'Only letters, numbers, and periods allowed' });
                    return;
                }
                if (get().isNicknameSubmitting) return;

                set({ isNicknameSubmitting: true, nicknameError: '' });

                try {
                    const userId = useAuthStore.getState().userId;
                    await updateProfileNickname(userId ?? '', nickname);
                    await refreshOwnSocialProfile(userId);

                    const preferences = await fetchPreferences();

                    set({
                        nicknameInput: nickname,
                        shouldForcePreferencesSubmission: true,
                        step: 'preferences',
                        initialLevel: resolveInitialLevel(preferences),
                        selectedLevel: resolveSelectedLevel(preferences),
                    });
                } catch (error) {
                    const serverError = error as ServerError;
                    set({
                        nicknameError: serverError?.error
                            ? getErrorMessage(serverError)
                            : 'Could not save nickname. Please try again.',
                    });
                } finally {
                    set({ isNicknameSubmitting: false });
                }
            },
            setLevel: (level) => set({ selectedLevel: level }),
            submitPreferences: async () => {
                if (get().isSubmittingPreferences) return;

                const level = get().selectedLevel;
                const hasChanges = level !== get().initialLevel;
                if (!hasChanges && !get().shouldForcePreferencesSubmission) {
                    trackPrivacyLevel(level, false);
                    advanceToAvatarStep();
                    return;
                }

                set({ isSubmittingPreferences: true, metricsError: '' });

                try {
                    // Full overwrite of the nine managed keys (+ the volume write key),
                    // mirroring mobile and `useSetSharingLevel` — POST every target, not a delta.
                    for (const [option, value] of Object.entries(getSharingLevelTargets(level))) {
                        await updatePreference(option as keyof SocialPreferencesVo, value);
                    }

                    // Reconcile the React Query prefs cache so the rest of the app
                    // (profile gating, leaderboards) sees the writes we just made.
                    await getQueryClient().invalidateQueries({ queryKey: socialPreferencesKeys.all });

                    set({ initialLevel: level, shouldForcePreferencesSubmission: false });

                    trackPrivacyLevel(level, true);
                    advanceToAvatarStep();
                } catch (error) {
                    const serverError = error as ServerError | undefined;
                    set({
                        metricsError: serverError?.error
                            ? getErrorMessage(serverError)
                            : 'Could not update sharing preferences. Please try again.',
                    });
                    if (serverError) handleSnackbarError(serverError);
                } finally {
                    set({ isSubmittingPreferences: false });
                }
            },
            shuffleAvatar: () => {
                if (get().isSubmittingAvatar) return;
                avatarChanged = true;
                const shuffleIndex = get().shuffleIndex + 1;
                const userId = useAuthStore.getState().userId;
                const cachedSocialProfile = getQueryClient().getQueryData<GetSocialProfileMetadataResponse>(
                    ownSocialProfileQueryOptions(userId).queryKey,
                );
                const seed = cachedSocialProfile?.social_profile?.nickname || get().nicknameInput || userId || 'kalshi';
                set({
                    pendingAvatarDataUrl: null,
                    avatarError: '',
                    shuffleIndex,
                    generatedAvatarUrl: getGeneratedProfileImageUrl(seed, shuffleIndex),
                });
            },
            selectAvatarFromFile: async (file) => {
                if (get().isSubmittingAvatar) return;
                set({ avatarError: '' });
                // The file read and the image compression are both unbounded, so a logout
                // can land between the user picking an image and it arriving here. dispose()
                // has cleared the flow by then, and this would put the previous person's
                // photo in front of whoever signs in next.
                const generation = flowGeneration;

                try {
                    const base64 = await readFileAsDataUrl(file);
                    const dataUrl = await compressImage(base64, 512, 512, 1);
                    if (typeof dataUrl !== 'string') throw new Error('invalid_image');
                    if (generation !== flowGeneration) return;
                    avatarChanged = true;
                    set({ pendingAvatarDataUrl: dataUrl });
                } catch (error) {
                    set({ avatarError: 'Could not process the selected image.' });
                }
            },
            submitAvatar: async () => {
                if (get().isSubmittingAvatar) return;

                const userId = useAuthStore.getState().userId;
                const cachedSocialProfile = getQueryClient().getQueryData<GetSocialProfileMetadataResponse>(
                    ownSocialProfileQueryOptions(userId).queryKey,
                );
                if (
                    !avatarChanged &&
                    hasRealProfileImage(cachedSocialProfile?.social_profile?.profile_image_path ?? null)
                ) {
                    get().actions.close(true);
                    return;
                }

                set({ isSubmittingAvatar: true, avatarError: '' });

                try {
                    const uploadedDataUrl = get().pendingAvatarDataUrl;
                    if (uploadedDataUrl) {
                        await updatePendingProfileImage(userId ?? '', uploadedDataUrl);
                    } else {
                        await updateGeneratedProfileImage(userId ?? '', get().shuffleIndex);
                    }

                    await refreshOwnSocialProfile(userId);

                    get().actions.close(true);
                } catch (error) {
                    const serverError = error as ServerError | undefined;
                    if (serverError) handleSnackbarError(serverError);
                    set({ avatarError: 'Could not save your profile picture.' });
                } finally {
                    set({ isSubmittingAvatar: false });
                }
            },
        },
    }),
);
